import React, { useEffect, useMemo, useState } from "react"
import styles from "./modalOutboundCampaign.module.css"
import Button from "../../Form/Button"
import Select from "../../Form/Select"
import FormGroupInline from '../../Form/FormGroupInline'
import FormGroup from "../../Form/FormGroup"
import BreakForm from "../../Form/BreakForm"
import InputFile from "../../Form/InputFile"
import { toast } from "react-toastify"
import { WhatsAppTemplatePreview } from "../../WhatsAppTemplateCard"

const normalizeMappingLabel = value => `${value || ""}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "")

const ConfigMessage = ({ data, fields = [], sampleRow = {}, templates = [], selectedTemplate = {}, initialMessage = {}, handleChangeValue = () => { }, handleChangeStep = () => { }, onSubmit = () => { } }) => {
    const [formattedTemplate, setFormattedTemplate] = useState({})
    const validFields = useMemo(() => {
        return (Array.isArray(fields) ? fields : []).filter(field => field && field.id !== undefined && field.id !== null)
    }, [fields])
    const fieldsFormatted = useMemo(() => validFields.map(field => ({
        ...field,
        value: `${field.value}${sampleRow?.[field.id] !== undefined && sampleRow?.[field.id] !== "" ? ` · Ex.: ${sampleRow[field.id]}` : ""}`,
    })), [sampleRow, validFields])
    const templatesFormatted = useMemo(() => {
        return (Array.isArray(templates) ? templates : [])
            .filter(template => template?.template_id)
            .map(template => ({ id: template.template_id, value: `${template.name} - ${template.category} (${template.language})` }))
    }, [templates])
    const [formatHeaderTemplate, setFormatHeaderTemplate] = useState()
    const [variablesHeader, setVariablesHeader] = useState([])
    const [variablesBody, setVariablesBody] = useState([])
    const [variablesButtons, setVariablesButtons] = useState([])
    const hasTemplateVariables = variablesHeader.length > 0 || variablesBody.length > 0 || variablesButtons.length > 0

    const inferVariableField = variable => {
        const normalizedVariable = normalizeMappingLabel(variable)
        if (!normalizedVariable || /^\d+$/.test(normalizedVariable)) return ""
        return validFields.find(field => {
            const normalizedField = normalizeMappingLabel(field.value)
            return normalizedField === normalizedVariable || normalizedField.includes(normalizedVariable) || normalizedVariable.includes(normalizedField)
        })?.id || ""
    }

    const getInitialMapping = (group, key) => {
        if (group === "header") return initialMessage?.content?.header?.variables?.find(variable => variable.key === key)?.value || ""
        if (group === "body") return initialMessage?.content?.body?.variables?.find(variable => variable.key === key)?.value || ""
        return initialMessage?.content?.buttons?.[key]?.value || ""
    }

    useEffect(() => {
        if (selectedTemplate) {
            setFormattedTemplate({ ...selectedTemplate, type: "template" })
        }
    }, [selectedTemplate])

    useEffect(() => {
        verifyIfVariablesHeaderByText(selectedTemplate?.content?.header || {})
        verifyIfVariablesBodyByText(selectedTemplate?.content?.body || {})
        verifyIfVariablesButtons(selectedTemplate?.content?.buttons || [])
    }, [selectedTemplate?.content])

    useEffect(() => {
        setFormatHeaderTemplate(selectedTemplate?.content?.header?.format)
    }, [selectedTemplate?.content?.header?.format])

    const verifyIfVariablesHeaderByText = (header) => {
        const text = header?.text || ""
        const variableMatches = [...text.matchAll(/{{(\d+)}}/g)]

        let extractedVariables = variableMatches.map((match) => ({
            key: `{{${match[1]}}}`,
            name: match[1],
            value: getInitialMapping("header", `{{${match[1]}}}`),
            example: "",
        }))

        if (header?.examples && header.examples.length === extractedVariables.length) {
            extractedVariables = extractedVariables.map((variable, index) => ({
                ...variable,
                example: header.examples[index],
            }))
        }

        setVariablesHeader(extractedVariables)
    }

    const verifyIfVariablesBodyByText = (body) => {
        const text = body?.text || ""
        const variableMatches = [...text.matchAll(/{{(\w+)}}/g)]

        let extractedVariables = variableMatches.map((match) => ({
            key: `{{${match[1]}}}`,
            name: match[1],
            value: getInitialMapping("body", `{{${match[1]}}}`) || inferVariableField(match[1]),
            example: "",
        }))

        if (body?.examples && body.examples.length === extractedVariables.length) {
            extractedVariables = extractedVariables.map((variable, index) => ({
                ...variable,
                example: body.examples[index],
            }))
        }

        setVariablesBody(extractedVariables)
    }

    const verifyIfVariablesButtons = (buttons) => {
        let extractedVariables = buttons?.map((button, index) => (
            {
                key: index,
                type: button?.type,
                text: button?.text,
                url: button?.url,
                value: getInitialMapping("buttons", index) || inferVariableField(button?.text),
                example: button?.examples?.[0]
            }
        ))?.filter(button => (
            button?.type === "COPY_CODE"
            || (button?.type === "URL" && /{{\w+}}/.test(button?.url || ""))
        )) || []

        setVariablesButtons(extractedVariables)
    }

    const handleSetMedia = (file) => {
        if (file[0]) {
            onChangeHeaderTemplate("file", file[0])
            onChangeHeaderTemplate("url", URL.createObjectURL(file[0]))
            onChangeHeaderTemplate("filename", file[0]?.name)
        }
    }

    const onChangeHeaderTemplate = (name, value) => {
        setFormattedTemplate(previousValue => {
            const newValue = {
                ...previousValue,
                content: {
                    ...previousValue?.content,
                    header: {
                        ...previousValue?.content?.header,
                        [name]: value,
                    }
                }
            }
            return newValue
        })
    }

    const onChangeVariablesHeader = (key, value) => {
        setVariablesHeader(previousValue => previousValue?.map(variable => {
            if (variable?.key === key) {
                return {
                    ...variable,
                    value
                }
            }
            return variable
        }))
    }

    const onChangeVariablesBody = (key, value) => {
        setVariablesBody(previousValue => previousValue?.map(variable => {
            if (variable?.key === key) {
                return {
                    ...variable,
                    value
                }
            }
            return variable
        }))
    }

    const onChangeVariablesButtons = (key, value) => {
        setVariablesButtons(previousValue => previousValue?.map(button => {
            if (button?.key === key) {
                return {
                    ...button,
                    value
                }
            }
            return button
        }))
    }

    const handleSubmit = () => {
        if (!data?.template_id || !formattedTemplate?.name) return toast.error("Selecione um template para continuar.")
        const missingVariable = [...variablesHeader, ...variablesBody, ...variablesButtons].find(variable => !variable?.value)
        if (missingVariable) return toast.error("Associe todas as variáveis a uma coluna da planilha.")

        let template = {
            ...formattedTemplate,
            content: {
                ...formattedTemplate.content,
                header: formattedTemplate.content?.header ? { ...formattedTemplate.content.header } : formattedTemplate.content?.header,
                body: formattedTemplate.content?.body ? { ...formattedTemplate.content.body } : formattedTemplate.content?.body,
                buttons: formattedTemplate.content?.buttons?.map(button => ({ ...button })) || [],
            },
        }
        const formatsFileRequired = ["IMAGE", "VIDEO", "DOCUMENT"]
        const templateHeader = formattedTemplate?.content?.header || {}
        if (formatsFileRequired.includes(templateHeader?.format) && !templateHeader?.file && !templateHeader?.handle) {
            return toast.error("Selecione uma mídia para o cabeçalho.")
        }
        if (formattedTemplate?.content?.header?.format === "LOCATION" && (!formattedTemplate?.content?.header?.location?.latitude || !formattedTemplate?.content?.header?.location?.longitude || !formattedTemplate?.content?.header?.location?.address)) {
            return toast.error("A localização é obrigatória!")
        }
        if (variablesHeader?.length > 0) {
            template.content = {
                ...template.content,
                header: {
                    ...template.content?.header,
                    variables: variablesHeader
                }
            }
        }
        if (variablesBody?.length > 0) {
            template.content = {
                ...template.content,
                body: {
                    ...template.content?.body,
                    variables: variablesBody
                }
            }
        }
        if (variablesButtons?.length > 0) {
            const buttons = template.content?.buttons?.map((button, index) => {
                const variable = variablesButtons?.find(variable => variable?.key === index)
                if (!variable) return button
                return {
                    ...button,
                    value: variable?.value
                }
            })
            template.content = {
                ...template.content,
                buttons,
            }
        }
        const formattedMessage = {
            type: "template",
            category: template?.category,
            content: {
                category: template?.category,
                template_name: template?.name,
                template_language: template?.language,
                header: template?.content?.header,
                body: template?.content?.body,
                footer: template?.content?.footer,
                buttons: template?.content?.buttons
            },
            upload: {
                file: template?.content?.header?.file
            }
        }
        onSubmit(formattedMessage)
    }

    const getSampleValue = variable => {
        if (variable?.value && sampleRow?.[variable.value] !== undefined && sampleRow?.[variable.value] !== "") return `${sampleRow[variable.value]}`
        return `${variable?.example || variable?.key || ""}`
    }

    const previewTemplate = useMemo(() => {
        const nextContent = {
            ...formattedTemplate.content,
            header: formattedTemplate.content?.header ? { ...formattedTemplate.content.header } : formattedTemplate.content?.header,
            body: formattedTemplate.content?.body ? { ...formattedTemplate.content.body } : formattedTemplate.content?.body,
            buttons: formattedTemplate.content?.buttons?.map(button => ({ ...button })) || [],
        }
        if (nextContent?.header?.text) {
            nextContent.header.text = variablesHeader.reduce((text, variable) => text.replaceAll(variable.key, getSampleValue(variable)), nextContent.header.text)
        }
        if (nextContent?.body?.text) {
            nextContent.body.text = variablesBody.reduce((text, variable) => text.replaceAll(variable.key, getSampleValue(variable)), nextContent.body.text)
        }
        variablesButtons.forEach(variable => {
            if (nextContent.buttons?.[variable.key]) nextContent.buttons[variable.key].value = getSampleValue(variable)
        })
        return { ...formattedTemplate, content: nextContent }
    }, [formattedTemplate, sampleRow, variablesBody, variablesButtons, variablesHeader])

    return (
        <>
            <div className={styles.stepIntro}>
                <strong>Escolha e personalize a mensagem</strong>
                <span>Associe cada variável a uma coluna. A prévia usa os dados da primeira linha válida da planilha para mostrar o resultado.</span>
            </div>
            <FormGroup labelText="Template">
                <Select multiple={false} name="Template" placeholder="Selecione um template" options={templatesFormatted || []} selected={data?.template_id ?? ""} onChange={(value) => handleChangeValue("template_id", value)} />
            </FormGroup>
            {data?.template_id &&
                <>
                    <div className={styles.container}>
                        {(formatHeaderTemplate === "IMAGE" || formatHeaderTemplate === "VIDEO" || formatHeaderTemplate === "DOCUMENT") &&
                            <FormGroup labelText="Mídia do cabeçalho">
                                <InputFile onChange={handleSetMedia} value={""} multiple={false} />
                                {formattedTemplate?.content?.header?.file?.name && <span className={styles.filename}>{formattedTemplate?.content?.header?.file?.name}</span>}
                                {!formattedTemplate?.content?.header?.file && formattedTemplate?.content?.header?.handle && <span className={styles.filename}>Se não selecionar outra mídia, será usada a amostra do template.</span>}
                            </FormGroup>
                        }
                        <div className={styles.groupMessage}>
                            <WhatsAppTemplatePreview template={previewTemplate} time="12:45" />
                        </div>
                    </div>
                    {hasTemplateVariables ?
                        <>
                            <BreakForm title="Campos variáveis" showAction={false} />
                            <div className={styles.variables}>
                            {variablesHeader?.length > 0 &&
                                <FormGroup labelText="Cabeçalho">
                                    {variablesHeader.map(variable => (
                                        <FormGroupInline key={variable?.key}>
                                            <span className={styles.label}>{variable?.key}</span>
                                            <Select multiple={false} name={`Selecione o valor para ${variable?.key}`} placeholder="Selecione um campo" options={fieldsFormatted || []} selected={variable?.value ?? ""} onChange={(value) => onChangeVariablesHeader(variable?.key, value)} />
                                        </FormGroupInline>
                                    ))}
                                </FormGroup>
                            }
                            {variablesBody?.length > 0 &&
                                <FormGroup labelText="Corpo da mensagem">
                                    {variablesBody.map(variable => (
                                        <FormGroupInline key={variable?.key}>
                                            <span className={styles.label}>{variable?.key}</span>
                                            <Select multiple={false} name={`Selecione o valor para ${variable?.key}`} placeholder="Selecione um campo" options={fieldsFormatted || []} selected={variable?.value ?? ""} onChange={(value) => onChangeVariablesBody(variable?.key, value)} />
                                        </FormGroupInline>
                                    ))}
                                </FormGroup>
                            }
                            {variablesButtons?.length > 0 &&
                                <FormGroup labelText="Botões">
                                    {variablesButtons.map(button => (
                                        <FormGroupInline key={button?.key}>
                                            <span className={styles.label}>{`Botão '${button?.text}', ${button?.url ? `variável {{1}} da URL ${button?.url}` : `ex: ${button?.example}`}`}</span>
                                            <Select multiple={false} name={`Selecione o valor para o botão`} placeholder="Selecione um campo" options={fieldsFormatted || []} selected={button?.value ?? ""} onChange={(value) => onChangeVariablesButtons(button?.key, value)} />
                                        </FormGroupInline>
                                    ))}
                                </FormGroup>
                            }
                            </div>
                        </>
                        :
                        <div className={styles.noVariablesNotice}>
                            <strong>Nenhum campo variável</strong>
                            <span>Este template não possui variáveis. Nenhum preenchimento adicional é necessário.</span>
                        </div>
                    }
                </>
            }
            <FormGroupInline>
                <Button variant='secondary' onClick={() => handleChangeStep(3)}>Voltar aos contatos</Button>
                <Button variant='primary' onClick={handleSubmit}>Confirmar campos</Button>
            </FormGroupInline>
        </>
    )
}

export default ConfigMessage
